import { ShaderMaterial } from 'three';

// import glslRectSDF from '@/libs/lygia/sdf/rectSDF.glsl';
import { materialEasyUniforms, simpleFs, simpleVs } from '@resn/gozer-three';

import {
    shaderChunks as ShaderChunksMatcap,
    uniforms as uniformsMatcap,
} from '../shaders/ShaderChunks/matcap';
import { shaderChunks as ShaderChunksPreReveal } from '../shaders/ShaderChunks/pre-reveal';
import {
    shaderChunks as glslBurn,
    uniforms as uniformsBurn,
} from '../shaders/ShaderChunks/transitions/burn';
import {
    defines as definesTransitionCommon,
    shaderChunks as glslTransitionCommon,
} from '../shaders/ShaderChunks/transitions/common';
import { shaderChunks as glslShine } from '../shaders/ShaderChunks/transitions/shine';
import { shaderChunks as ShaderChunksWipe } from '../shaders/ShaderChunks/wipe-reveal';
import { neutralTonemappingChunks as glslNeutralTonemapping } from '@/components/gl/shaders/ShaderChunks/neutral-tonemapping.js';
import glslOverlay from '@/libs/lygia/color/blend/overlay.glsl';

class SlabMaterial extends ShaderMaterial {
    options = {
        name: 'SlabMaterial',
        transparent: true,
    };

    constructor({ uniforms = {}, options = {} } = {}) {
        Object.keys(uniforms).forEach((key) => {
            uniforms[key] = { value: uniforms[key] };
        });

        super({
            uniforms: {
                ...uniformsMatcap(),
                ...uniformsBurn(),
                ...ShaderChunksWipe.uniforms(),
                ...ShaderChunksPreReveal.uniforms(),
                ...uniforms,

                USE_TRANSITION_WIPE: { value: true },
                USE_TRANSITION_BURN: { value: false },
                USE_TRANSITION_SHINE: { value: false },
                TRANSITION_MODE: { value: 0 },
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
        });

        // Match defines naming with GoldenTicketMaterial
        this.defines = {
            USE_UV: true,
            USE_PRE_REVEAL: true,
            // USE_TRANSITION_WIPE: true,
            // USE_TRANSITION_BURN: false,
            // USE_TRANSITION_SHINE: false,
            // TRANSITION_MODE: 0,
            // ...definesTransitionCommon,

            // USE_DEBUG: true,
        };

        this.options = { ...this.options, ...options };

        this.type = this.options.name;
        this.extensions.derivatives = true;

        Object.keys(this.options).forEach((key) => (this[key] = this.options[key]));
        materialEasyUniforms(this);
    }

    clearTransitionUniforms({ transition = false } = {}) {
        if (transition) {
            Object.keys(this.uniforms).forEach((key) => {
                if (key.startsWith('USE_TRANSITION')) {
                    this.uniforms[key].value = false;
                }
            });
        }
    }
}

export default SlabMaterial;

const vertexShader = /* glsl */ `
    // Vertex Shader
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    varying vec2 vUv;
    varying vec2 vUv1;
    varying vec3 vEyeVector;
    varying vec3 vWorldNormal;
    varying vec4 vClipPosition;
    varying vec3 vTransformed;
    varying vec3 vWorldPosition;
    attribute vec2 uv1;
    uniform float uTime;

    uniform bool USE_TRANSITION_WIPE;
    uniform bool USE_TRANSITION_BURN;
    uniform bool USE_TRANSITION_SHINE;
    uniform int TRANSITION_MODE;

    ${glslTransitionCommon.uniforms}
    ${glslBurn.uniforms}

    ${glslTransitionCommon.funcs}
    ${glslBurn.vertex}

    ${glslShine.uniforms}
    
    void main() {
        // Transform the normal to world space
        vNormal = normalize(normalMatrix * normal);
        vUv = uv;
        vUv1 = uv1;

        // Calculate the view position in world space
        vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = viewPosition.xyz;

        vec3 transformed = position;
        vTransformed = transformed;

        if (USE_TRANSITION_BURN) {
            transformed = transition_burn_vertex(transformed, normal, 1.0, uTime);
        }

        vec4 worldPosition = modelMatrix * vec4(transformed, 1.0);
        vEyeVector = normalize(worldPosition.xyz - cameraPosition);
        vWorldNormal = normalize(modelViewMatrix * vec4(normal, 0.0)).xyz;
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        
        vClipPosition = gl_Position;
    }
`;

const fragmentShader = /* glsl */ `
    // Fragment Shader
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    varying vec2 vUv;
    varying vec2 vUv1;
    varying vec3 vEyeVector;
    varying vec3 vWorldNormal;
    varying vec4 vClipPosition;
    varying vec3 vTransformed;
    varying vec3 vWorldPosition;
    
    uniform sampler2D uAlphaMask;
    uniform sampler2D uNoise;
    uniform sampler2D uEnvMap;
    uniform float uTime;
    uniform float uAlpha;
    uniform float uInProgress;
    uniform float uIor;
    uniform float uDispersionAmount;
    uniform float uMatcapRotation;
    uniform float uDepthOffset;
    uniform float uOutlineScale;
    uniform float uLightsDownPr;

    uniform bool USE_TRANSITION_WIPE;
    uniform bool USE_TRANSITION_BURN;
    uniform bool USE_TRANSITION_SHINE;
    uniform int TRANSITION_MODE;

    #define PI 3.1415926535897932384626433832795
    #define NUM_COLORS 3
    uniform vec3 uColorPalette[NUM_COLORS];

    uniform vec2 uRes;

    ${glslOverlay}

    ${ShaderChunksMatcap.uniforms}
    ${ShaderChunksMatcap.funcs}
    ${ShaderChunksWipe.funcs}
    ${ShaderChunksPreReveal.funcs}
    ${glslNeutralTonemapping.funcsVariant}
    ${glslTransitionCommon.funcs}

    ${glslBurn.uniforms}
    ${glslBurn.funcs}

    ${glslShine.uniforms}
    ${glslShine.funcs}

    void main() {

        // Screen UV
        vec2 screenUv = (vClipPosition.xy / vClipPosition.w + 1.0) / 2.0;

        vec3 normal = normalize(vNormal);
        vec3 viewDir = normalize(-vViewPosition); // The eye ray is from the fragment to the camera
        float dotProduct = dot(normal, viewDir);
        vec3 refracted = refract(vEyeVector, vWorldNormal, 1.0/uIor);
        vec3 reflectedDir = reflect(viewDir, normal);

        // EnvMap
        vec4 envColor = texture2D(uEnvMap, reflectedDir.xy);

        // Matcap version 1
        vec3 matcapNormal = vNormal;
        vec3 matcapColor = matcap(tMatcap, matcapNormal, vViewPosition, false, 1.0, 1.0, 1.0, toRadians(uMatcapRotation));

        vec4 outPreReveal;
        #ifdef USE_PRE_REVEAL
            outPreReveal.rgb = preReveal(vUv, uOutlineScale, uTexPreRevealSticker, 2.2, uTime, true);
            // Apply lights down effect to pre-reveal
            outPreReveal.rgb = mix(outPreReveal.rgb, vec3(0.0), uLightsDownPr);
            outPreReveal.a = uAlpha;
        #endif

        vec4 outWipe;
        if (USE_TRANSITION_WIPE) {
            outWipe = wipe(vUv, uWipeAngle, uWipeRevealPr, uPreRevealColor2, uPreRevealColor1, uPreRevealColor2, uNoise, uTime);
        }
            
        // Card Texture
        vec4 alphaTex = texture2D(uAlphaMask, vUv + refracted.xy * 0.1);
        float cardMask = 1.0 - alphaTex.r;

        // Main Output
        vec3 mainBaseColor = blendScreen(envColor.rgb, uColorPalette[1]);
        mainBaseColor = mix(vec3(1.0), mainBaseColor, 0.75);

        float mainAlpha = mix(0.25 * cardMask, 1.0, matcapColor.r);
        vec4 mainMatcap = vec4(mainBaseColor, mainAlpha);
        vec4 mainOutput = mainMatcap;

        if (USE_TRANSITION_SHINE) {
            mainOutput.rgb = transition_shine(mainOutput.xyz, vWorldPosition, 0.0, 0.5, 0.5);
            mainOutput.rgb = transition_shine(mainOutput.xyz, vWorldPosition, 0.0, 0.5, -0.25);
        }
        
        vec4 finalColor;
        
        #ifdef USE_PRE_REVEAL 
            if (USE_TRANSITION_WIPE) {
                // Mix pre-reveal and main output based on wipe progress
                finalColor = mix(outPreReveal, mainOutput, outWipe.a);
                // Blend with wipe color effect
                finalColor.rgb = blendScreen(finalColor.rgb, outWipe.rgb);
            }
        #else
            finalColor = mainOutput;
        #endif
        
        
        if (USE_TRANSITION_BURN) {
            vec4 burn = transition_burn(vTransformed, normal, uWipeRevealPr, uTime);
            finalColor = mix(outPreReveal, mainOutput, 1.0-burn.a);
            finalColor.rgb += burn.rgb;
        }
        
        finalColor.rgb = mix(vec3(0.0), finalColor.rgb, uInProgress);
        gl_FragColor = finalColor;

        #ifdef USE_DEBUG
            gl_FragColor = vec4(vec3(outWipe.a), 1.0);
        #endif

/*         vec4 finalColor = vec4(0.0);
        finalColor = mix(outPreReveal, mainOutput, outWipe.a);
        finalColor.rgb = blendScreen(finalColor.rgb, outWipe.rgb);
        finalColor.rgb = mix(vec3(0.0), finalColor.rgb, uInProgress);
        
        gl_FragColor = finalColor; */

        // Fog
        float fog = mix(1.0, 0.0, uDepthOffset);
        
        gl_FragColor.rgb = mix(vec3(0.0), gl_FragColor.rgb, fog);
        
        gl_FragColor.a *= uAlpha;
        
        // gl_FragColor = vec4(vUv.rg, 0.0, 1.0);
        // gl_FragColor = vec4(vec3(outline), 1.0);
    }
`;
